import {
  CAREPLAN_PRODUCT_FORMAT,
  CAREPLAN_PRODUCT_ROLE,
} from "@/domains/careplan/models/constants";
import { MOCK_CATALOG, type MockCatalogProduct } from "@/mocks/data/catalog";
import type { CareplanProductWire, PreviousCareplanProductWire } from "@/mocks/types";

const SHARED_PRODUCT_COPY = {
  brand: "Sample Botanicals",
  guaranteedSatisfaction: "Not the right fit? Every first order is covered by a 30-day refund.",
  howAndWhereMade:
    "Formulated by a clinical team and made in a certified facility. Every batch is tested by an independent lab before it ships.",
  shippingPolicy: "Ships free in discreet packaging within 2 business days.",
  importantSafetyNotes:
    "Do not drive or operate machinery until you know how this product affects you.",
};

const fromCatalog = (product: MockCatalogProduct) => {
  return {
    id: product.productId,
    name: product.name,
    sku: product.sku,
    variantId: product.variantId,
    price: product.priceInCents,
    images: [...product.images],
    summaryImage: product.summaryImage ?? null,
    rating: product.rating,
    reviewCount: product.reviewCount,
  };
};

export const DAILY_BALANCE_TINCTURE: CareplanProductWire = {
  ...SHARED_PRODUCT_COPY,
  ...fromCatalog(MOCK_CATALOG.DAILY_BALANCE),
  role: CAREPLAN_PRODUCT_ROLE.FOUNDATIONAL,
  forms: CAREPLAN_PRODUCT_FORMAT.GEL_TINCTURE,
  form: "Tincture",
  type: "tincture",
  description:
    "A low, steady dose taken every morning to keep everyday discomfort from building up.",
  whyRecommended: {
    title: "For Your Daily Back Pain",
    content:
      "This sample plan starts with a small daily dose because **consistency** usually matters more than strength. Taking it at the same time every morning keeps a gentle baseline, so you reach for as-needed relief less often.",
  },
  usageInstructions:
    "Take **0.5 mL** under the tongue every morning with breakfast. Hold it for 60 seconds before swallowing.",
  dose: "0.5 mL",
  increaseDose: "If nothing changes after 7 days, message your care team before going up to 1 mL.",
  cannaboidContent: "10 mg CBD and 1 mg THC per 0.5 mL",
  cbdThcRatio: "10:1",
  thcCbdType: "Full spectrum",
  effectOnset: "30-60 min",
  effectDuration: "6-8 hours",
  timesOfUse: "Morning",
  symptoms: "Pain, Stress",
  productSupply: "30-day supply in a 15 mL bottle.",
  allIngredients: "MCT oil, hemp extract, natural peppermint flavor",
  facts:
    "**Per 0.5 mL serving:** 10 mg CBD, 1 mg THC.\n\n**Other ingredients:** MCT oil, natural peppermint flavor.",
  howItWorks:
    "Cannabinoids work with the body's endocannabinoid system, which helps regulate comfort, mood and sleep. A small dose taken at the same time each day keeps levels steady.",
};

export const QUICK_EASE_INHALER: CareplanProductWire = {
  ...SHARED_PRODUCT_COPY,
  ...fromCatalog(MOCK_CATALOG.QUICK_EASE),
  role: CAREPLAN_PRODUCT_ROLE.ADHOC,
  forms: CAREPLAN_PRODUCT_FORMAT.INHALER,
  form: "Inhaler",
  type: "inhaler",
  description: "A metered inhaler for moments when you need relief within minutes.",
  whyRecommended: {
    title: "For Your Sudden Pain Spikes",
    content:
      "Keep this sample inhaler nearby for the moments your daily dose does not cover. Each puff is **precisely metered**, so it is easy to stay within your plan.",
  },
  usageInstructions:
    "Use **1 to 2 puffs** when you need relief. Wait **15 minutes** between puffs and use no more than **6 puffs** a day.",
  dose: "1-2 puffs",
  increaseDose: null,
  cannaboidContent: "2 mg CBD and 1 mg THC per puff",
  cbdThcRatio: "2:1",
  thcCbdType: "Broad spectrum",
  effectOnset: "2-5 min",
  effectDuration: "2-3 hours",
  timesOfUse: "As needed",
  symptoms: "Pain flare-ups",
  productSupply: "About 200 metered puffs.",
  allIngredients: "Hemp extract, propellant-free carrier",
  facts: "**Per puff:** 2 mg CBD, 1 mg THC.",
  howItWorks:
    "Inhaled cannabinoids reach the bloodstream through the lungs, so the effect starts within minutes and fades within a few hours.",
};

const NIGHT_REST_GUMMIES: CareplanProductWire = {
  ...SHARED_PRODUCT_COPY,
  ...fromCatalog(MOCK_CATALOG.NIGHT_REST),
  role: CAREPLAN_PRODUCT_ROLE.BEDTIME,
  forms: CAREPLAN_PRODUCT_FORMAT.GUMMIES,
  form: "Gummies",
  type: "gummy_edible",
  description: "A slow, gentle gummy that helps you wind down and stay asleep.",
  whyRecommended: {
    title: "For Your Nightly Flares",
    content:
      "Edibles take longer to start but last through the night, which suits a **bedtime routine**. This sample recommendation pairs a modest dose with a fixed time.",
  },
  usageInstructions:
    "Take **1 gummy** about 60 minutes before bed. Do not take a second gummy the same night.",
  dose: "1 gummy",
  increaseDose: "Ask your care team before trying a second gummy.",
  cannaboidContent: "5 mg CBD and 2.5 mg THC per gummy",
  cbdThcRatio: "2:1",
  thcCbdType: "Full spectrum",
  effectOnset: "45-90 min",
  effectDuration: "6-8 hours",
  timesOfUse: "Bedtime",
  symptoms: "Trouble sleeping",
  productSupply: "30 gummies.",
  allIngredients: "Cane sugar, tapioca syrup, pectin, hemp extract, natural berry flavor",
  facts: "**Per gummy:** 5 mg CBD, 2.5 mg THC.",
  howItWorks:
    "Digested cannabinoids are released slowly by the liver, which gives a longer, steadier effect that fits the hours you sleep.",
};

export const MORNING_FOCUS_CAPSULES: PreviousCareplanProductWire = {
  id: MOCK_CATALOG.MORNING_FOCUS.productId,
  variantId: MOCK_CATALOG.MORNING_FOCUS.variantId,
  name: MOCK_CATALOG.MORNING_FOCUS.name,
  price: MOCK_CATALOG.MORNING_FOCUS.priceInCents,
  image: MOCK_CATALOG.MORNING_FOCUS.images[0],
  role: CAREPLAN_PRODUCT_ROLE.FOUNDATIONAL,
  description: "A daytime capsule from an earlier version of this sample plan.",
  whyRecommended: "Replaced by Daily Balance Tincture for faster, easier dose changes",
};

export const CURRENT_CAREPLAN_PRODUCTS = [
  DAILY_BALANCE_TINCTURE,
  QUICK_EASE_INHALER,
  NIGHT_REST_GUMMIES,
];
